import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Share2, Download, Loader2, Globe, Music } from 'lucide-react';
import type { Keyboard } from '@/lib/microtonal/types';
import { useSharedKeyboards, usePublishKeyboard } from '@/hooks/useMicrotonalNostr';
import { useMicrotonalLibrary } from '@/hooks/useMicrotonalLibrary';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';
import { frequencyForNote, degreesPerPeriod } from '@/lib/microtonal/scale';
import { nip19 } from 'nostr-tools';
import { cn } from '@/lib/utils';

export function ShareKeyboards() {
  const { activeKeyboard, importKeyboard, saveKeyboard } = useMicrotonalLibrary();
  const { data: sharedKeyboards, isLoading } = useSharedKeyboards();
  const { user } = useCurrentUser();
  const { toast } = useToast();

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [dTag, setDTag] = useState('');

  const publishMutation = usePublishKeyboard();

  const handleShare = async () => {
    if (!user) {
      toast({ title: 'Login required', description: 'Please log in with Nostr to share keyboards.' });
      return;
    }
    if (!dTag.trim()) {
      toast({ title: 'Identifier required', description: 'Enter a unique d-tag identifier for your keyboard.' });
      return;
    }
    try {
      const event = await publishMutation.mutateAsync({ keyboard: activeKeyboard, dTag: dTag.trim() });
      const naddr = nip19.naddrEncode({
        kind: event.kind,
        pubkey: event.pubkey,
        identifier: dTag.trim(),
      });
      toast({ 
        title: 'Keyboard shared!', 
        description: `Share link: ${window.location.origin}/${naddr}` 
      });
      setShareDialogOpen(false);
    } catch (err) {
      toast({ title: 'Publish failed', description: String(err) });
    }
  };

  const handleImport = (keyboard: Keyboard) => {
    const imported = importKeyboard(keyboard);
    toast({ title: 'Imported', description: `"${imported.name}" added to your library` });
  };

  return (
    <div className="space-y-6">
      {/* Share current keyboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Share2 className="size-5" /> Share Your Keyboard</CardTitle>
          <CardDescription>
            Publish your current keyboard ({activeKeyboard.name}) to Nostr so others can discover and use it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Button onClick={() => setShareDialogOpen(true)} disabled={!user}>
              <Share2 className="size-4 mr-2" /> Share to Nostr
            </Button>
            {!user && <span className="text-sm text-muted-foreground">Log in to share.</span>}
          </div>
        </CardContent>
      </Card>

      {/* Browse shared keyboards */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Globe className="size-5" /> Community Keyboards</CardTitle>
          <CardDescription>Keyboards shared by the Nostr community. Click to import.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" /> Loading shared keyboards…
            </div>
          ) : sharedKeyboards && sharedKeyboards.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sharedKeyboards.map(({ event, keyboard }) => (
                <SharedKeyboardCard
                  key={event.id}
                  event={event}
                  keyboard={keyboard}
                  onImport={() => handleImport(keyboard)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Music className="size-12 mx-auto mb-3 opacity-50" />
              <p>No keyboards shared yet. Be the first!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Share dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share "{activeKeyboard.name}"</DialogTitle>
            <DialogDescription>
              Enter a unique identifier (d-tag) for your keyboard. This will be published as a kind 37357 Nostr event.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="dtag">Identifier</Label>
              <Input
                id="dtag"
                placeholder="e.g. my-31-edo-keyboard"
                value={dTag}
                onChange={(e) => setDTag(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
              />
              <p className="text-xs text-muted-foreground">
                Use lowercase letters, numbers, and hyphens. This creates a permanent shareable link.
              </p>
            </div>
            <div className="rounded-lg bg-muted p-3 text-sm">
              <div className="font-medium">Keyboard details:</div>
              <div className="text-muted-foreground mt-1">
                {keyboard.scale.steps.length + 1} degrees per period · {keyboard.scale.periodCents}c period · {keyboard.keys.length} keys
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleShare} disabled={publishMutation.isPending}>
              {publishMutation.isPending && <Loader2 className="size-4 mr-2 animate-spin" />}
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SharedKeyboardCard({ event, keyboard, onImport }: { 
  event: NostrEventShape; 
  keyboard: Keyboard; 
  onImport: () => void;
}) {
  const author = useAuthor(event.pubkey);
  const authorName = author.data?.metadata?.name ?? event.pubkey.slice(0, 12);
  const dpp = degreesPerPeriod(keyboard.scale);

  // Generate a few frequencies for display
  const sampleFreqs = keyboard.keys.slice(0, 8).map((k) => 
    frequencyForNote(keyboard.scale, k.degree, k.period).toFixed(0)
  );

  return (
    <div className="rounded-lg border p-4 space-y-3 hover:border-primary/50 transition-colors">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-medium">{keyboard.name}</div>
          <div className="text-xs text-muted-foreground">by {authorName}</div>
        </div>
      </div>
      
      {keyboard.description && (
        <p className="text-sm text-muted-foreground line-clamp-2">{keyboard.description}</p>
      )}

      <div className="flex flex-wrap gap-1.5">
        <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
          {dpp} degrees
        </span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
          {keyboard.scale.periodCents}c period
        </span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
          {keyboard.keys.length} keys
        </span>
      </div>

      <div className="text-xs font-mono text-muted-foreground truncate">
        {sampleFreqs.join(' · ')} Hz
      </div>

      <Button size="sm" variant="outline" onClick={onImport} className="w-full">
        <Download className="size-4 mr-1" /> Import
      </Button>
    </div>
  );
}

type NostrEventShape = {
  id: string;
  pubkey: string;
  created_at: number;
  kind: number;
};
